import React from "react";
import { useNavigate } from "react-router-dom";
import { getPageNumbers } from "./helpers";
import { toast } from "react-toastify";

// ─── Empty State ──────────────────────────────────────────────────────────────
const EmptyState = () => {
    const navigate = useNavigate();
    return (
        <div className="dropdown-empty">
            <p>No photographer found</p>
            <button
                type="button"
                className="btn-primary"
                onClick={() => navigate("/photographer")}
            >
                + Add New Photographer
            </button>
        </div>
    );
};

// ─── Pagination ───────────────────────────────────────────────────────────────
const Pagination = ({ page, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;

    return (
        <div className="pagination">
            <button
                type="button"
                className="pagination-btn"
                disabled={page === 1}
                onClick={() => onPageChange(page - 1)}
            >
                Prev
            </button>

            {getPageNumbers(totalPages).map((p) => (
                <button
                    type="button"
                    key={p}
                    className={`pagination-btn ${p === page ? "active" : ""}`}
                    onClick={() => onPageChange(p)}
                >
                    {p}
                </button>
            ))}

            <button
                type="button"
                className="pagination-btn"
                disabled={page === totalPages}
                onClick={() => onPageChange(page + 1)}
            >
                Next
            </button>
        </div>
    );
};

// ─── Amount calculation helpers ───────────────────────────────────────────────

// ─── Photographer Row ─────────────────────────────────────────────────────────
const PhotographerRow = ({ 
    photographer, 
    index, 
    page, 
    LIMIT, 
    isChecked, 
    isDisabled, 
    isLock, 
    onToggle,
    isAssignedToThisPkg,
    isSelectedInOtherRole,
    isAssignedToOtherPkg,
    baseAmount,
    overrides,
    onCommissionChange,
    onAmountChange
}) => {
    // Get effective commission for a photographer (override or default)
    const commissionVal = overrides[photographer._id]?.commission !== undefined 
        ? overrides[photographer._id].commission 
        : (photographer.commissionPercentage ?? "");

    // Get effective photographer amount (override or auto-calculated)
    let autoAmount = "";
    if (commissionVal !== "") {
        const pct = parseFloat(commissionVal);
        const veroCommission = baseAmount * (pct / 100);
        if (photographer.gstRegistered) {
            autoAmount = Math.round(baseAmount - veroCommission);
        } else {
            autoAmount = Math.round((baseAmount / 1.18) - veroCommission);
        }
    }
    
    const amountVal = overrides[photographer._id]?.amount !== undefined 
        ? overrides[photographer._id].amount 
        : autoAmount;

    console.log("Photographer:", photographer.name, "gstRegistered:", photographer.gstRegistered, "autoAmount:", autoAmount);

    return (
        <tr key={photographer._id} style={{ 
            backgroundColor: "transparent" 
        }}>
        <td>
            <div className="checkbox-sr-cell" style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <input
                    type="checkbox"
                    checked={isChecked || isAssignedToThisPkg || isAssignedToOtherPkg || isSelectedInOtherRole}
                    disabled={isLock || isAssignedToThisPkg || isAssignedToOtherPkg || isSelectedInOtherRole || isDisabled}
                    onChange={(e) => {
                        if (e.target.checked && (photographer.status?.toLowerCase() === 'inactive' || photographer.isActive === false)) {
                            toast.error("This photographer is inactive");
                            e.preventDefault();
                            return;
                        }
                        onToggle(photographer._id, e.target.checked);
                    }}
                />
                <span>{(page - 1) * LIMIT + index + 1}</span>
            </div>
        </td>

        <td>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <strong style={{ color: (isAssignedToThisPkg || isAssignedToOtherPkg || isSelectedInOtherRole) ? "#9ca3af" : "inherit" }}>
                    {photographer.name || "-"}
                    {isAssignedToOtherPkg && <span style={{ color: "red", marginLeft: "4px", fontWeight: "normal" }}>(Reserve)</span>}
                </strong>
                
                {/* Status Badges removed to just show disabled checkbox */}
            </div>
            {photographer.mobileNumber && (
                <div style={{ fontSize: "12px", color: "#666" }}>
                    📱 {photographer.mobileNumber}
                </div>
            )}
        </td>

        <td>
            {photographer.categories?.length > 0
                ? photographer.categories.map(cat => cat.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())).join(", ")
                : "-"}
        </td>

        <td>{photographer.level || photographer.expertiseLevel || "-"}</td>

        <td>{photographer.hasStudio ? (photographer.studioName || "Yes") : "No"}</td>
        
        {/* Commission % — editable, syncs with amount */}
        <td>
            <input
                type="number"
                className="amount-input"
                value={commissionVal}
                min="0"
                max="100"
                step="0.01"
                placeholder="-"
                disabled={isLock}
                onChange={(e) => onCommissionChange(photographer._id, photographer.gstRegistered, e.target.value)}
                style={{ width: "80px", padding: "4px", border: "1px solid #ccc", borderRadius: "4px" }}
            />
            <span style={{ marginLeft: "4px", fontSize: "12px", color: "#666" }}>%</span>
        </td>

        {/* Photographer Amount — editable, syncs with commission */}
        <td>
            <span style={{ marginRight: "4px", fontSize: "12px", color: "#666" }}>₹</span>
            <input
                type="number"
                className="amount-input"
                value={amountVal}
                min="0"
                step="1"
                placeholder="-"
                disabled={isLock}
                onChange={(e) => onAmountChange(photographer._id, photographer.gstRegistered, e.target.value)}
                style={{ width: "100px", padding: "4px", border: "1px solid #ccc", borderRadius: "4px" }}
            />
        </td>

        <td>
            {photographer.avgRating != null
                ? `⭐ ${Number(photographer.avgRating).toFixed(1)}`
                : "-"}
        </td>
    </tr>
    );
};

const PhotographerTable = ({
    photographers,
    page,
    totalPages,
    LIMIT,
    isLock,
    selectAllChecked,
    selectedIds,
    assignedIds = [],
    otherSelectedIds = [],
    onSelectAll,
    onToggle,
    onPageChange,
    maxCount = Infinity,
    baseAmount = 0,
    photographerOverrides = {},
    setPhotographerOverrides
}) => {

    // Handle commission % change → auto-update amount
    const handleCommissionChange = (photographerId, isGst, value) => {
        const pct = parseFloat(value);
        let amount = "";
        if (!isNaN(pct) && pct >= 0 && pct <= 100) {
            const veroCommission = baseAmount * (pct / 100);
            if (isGst) {
                amount = Math.round(baseAmount - veroCommission);
            } else {
                amount = Math.round((baseAmount / 1.18) - veroCommission);
            }
        }
        setPhotographerOverrides((prev) => ({
            ...prev,
            [photographerId]: { commission: value, amount: amount !== "" ? String(amount) : "" },
        }));
    };

    // Handle amount change → auto-update commission %
    const handleAmountChange = (photographerId, isGst, value) => {
        const amt = parseFloat(value);
        let commission = "";
        if (!isNaN(amt) && baseAmount > 0 && amt >= 0) {
            if (isGst) {
                commission = parseFloat((((baseAmount - amt) / baseAmount) * 100).toFixed(2));
            } else {
                commission = parseFloat(((((baseAmount / 1.18) - amt) / baseAmount) * 100).toFixed(2));
            }
            // Optional bounds check to avoid weird negative commission
            if (commission < 0) commission = 0;
            if (commission > 100) commission = 100;
        }
        setPhotographerOverrides((prev) => ({
            ...prev,
            [photographerId]: { amount: value, commission: commission !== "" ? String(commission) : "" },
        }));
    };

    if (photographers.length === 0) return <EmptyState />;

    return (
        <>
            <div className="table-responsive" style={{ overflowX: "auto" }}>
                <table className="photographer-table">
                    <thead>
                        <tr>
                            <th style={{ width: "80px" }}>
                                <div className="checkbox-sr-header">
                                    <input
                                        type="checkbox"
                                        onChange={onSelectAll}
                                        checked={
                                            selectAllChecked && photographers.length > 0
                                        }
                                        disabled={isLock || photographers.every(p => p.isAssigned) || assignedIds.length >= maxCount}
                                    />
                                    <span>Sr.</span>
                                </div>
                            </th>
                            <th>Photographer Name</th>
                            <th>Category</th>
                            <th>Level</th>
                            <th>Studio</th>
                            <th>Commission %</th>
                            <th>Photographer Amount (₹)</th>
                            <th>Avg. Rating</th>
                        </tr>
                    </thead>

                    <tbody>
                        {photographers.map((photographer, index) => {
                            const isSelected = selectedIds.includes(photographer._id);
                            const isAssignedToThisPkg = photographer.isAssigned;
                            const isSelectedInOtherRole = (otherSelectedIds || []).some(id => id.toString() === photographer._id.toString());

                            // 1. OR they are assigned to another package in the DB
                            // 2. OR they are already selected in a different role in this modal
                            const isAssignedToOtherPkg = photographer.isAssignedToOtherPkg;
                            const isQuotaFull = assignedIds.length >= maxCount;
                            const isDisabled = isAssignedToOtherPkg || isSelectedInOtherRole || (isQuotaFull && !isAssignedToThisPkg);

                            return (
                                <PhotographerRow
                                    key={photographer._id}
                                    photographer={photographer}
                                    index={index}
                                    page={page}
                                    LIMIT={LIMIT}
                                    isChecked={isSelected}
                                    isDisabled={isDisabled}
                                    isLock={isLock}
                                    onToggle={onToggle}
                                    isAssignedToThisPkg={isAssignedToThisPkg}
                                    isSelectedInOtherRole={isSelectedInOtherRole}
                                    isAssignedToOtherPkg={isAssignedToOtherPkg}
                                    baseAmount={baseAmount}
                                    overrides={photographerOverrides}
                                    onCommissionChange={handleCommissionChange}
                                    onAmountChange={handleAmountChange}
                                />
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <Pagination page={page} totalPages={totalPages} onPageChange={onPageChange} />
        </>
    );
};

export default PhotographerTable;
